import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadServiceDetails } from '../../store/directusService';
import { createTeam } from '../../store/userService';
import { formatFloat } from '../../utils/numberUtils';
import { clearKeys } from '../../utils/objectUtils';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import Button from '../formElements/Button';
import SingleSelect from '../formElements/SingleSelect';
import TextInputField from '../formElements/TextInputField';
import NotFound from '../notFound/NotFound';
import LoadingSpinner from '../ui/LoadingSpinner';

import { checkTeamName, initialErrorState, validate } from './validateCreateTeam';

import app from '../../app.module.css';
import form from '../../commonStyles/formStyles.module.css';

const CreateTeam = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        name: '',
        serviceId: '',
        costCode: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [isDisabled, setIsDisabled] = useState(false);
    const [keys, setKeys] = useState({ serviceId: '0' });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const serviceDetails = useSelector((state) => state.entities.directusService.serviceDetails);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        serviceDetails?.length < 1 && dispatch(loadServiceDetails());
    }, []);

    useEffect(() => {
        setIsDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(`Created team`)) {
            clearForm();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setIsDisabled(false);
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsDisabled(true);
        const { isValid, errors } = validate(newEntry);
        setErrors(errors);
        if (!isValid) return;
        const isExistingTeamName = await checkTeamName(newEntry.name, newEntry.serviceId);
        if (isExistingTeamName) {
            setErrors({
                ...errors,
                name: {
                    error: true,
                    message: `Team ${newEntry.name} already exists in this service.`
                }
            });
            return;
        }
        dispatch(createTeam(newEntry.name, newEntry));
    };

    // RENDER
    if (!hasRole([SUPERUSER], roles)) return <NotFound />;
    if (serviceDetails?.length < 1) return <LoadingSpinner content="No services" />;

    return (
        <div className={form.formWrapper} data-testid="form_start">
            <form className={form.form} onSubmit={onSubmit}>
                <h2 className={app.mainHeading}>Create Team</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id={'name'}
                            label={'Name'}
                            placeholder={'Enter team name'}
                            mandatory={true}
                            value={newEntry.name || ''}
                            error={errors.name}
                            onChange={(e) => onUpdate('name', e.target.value)}
                        />
                    </div>
                    <div className={form.formColumn}></div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            key={keys.serviceId}
                            id={'serviceId'}
                            label={'Service'}
                            placeholder="Search services..."
                            mandatory={true}
                            menuItems={serviceDetails || []}
                            selectedId={newEntry.serviceId || ''}
                            selected={
                                serviceDetails.find((el) => el.id === newEntry.serviceId) || {}
                            }
                            error={errors.serviceId}
                            onChange={(chosenId) => onUpdate('serviceId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <TextInputField
                            id={'costCode'}
                            label={'Cost Code'}
                            placeholder={'Enter cost code'}
                            value={newEntry.costCode || ''}
                            error={errors.costCode}
                            onChange={(e) => onUpdate('costCode', formatFloat(e.target.value))}
                        />
                    </div>
                </div>
                <Button id="createTeam" content="Create" disabled={isDisabled} />
            </form>
        </div>
    );
};

export default CreateTeam;
